/**
 * App Store screenshot display types and their accepted pixel dimensions.
 * Sourced from Apple's screenshot specifications; update here when Apple adds devices.
 * Each dimension pair is [width, height]; both orientations are listed explicitly.
 */
export interface ScreenshotSpec {
  displayType: string;
  label: string;
  device: 'iphone' | 'ipad' | 'mac' | 'appletv' | 'vision';
  /** Apple requires at least one screenshot for these classes when the app supports the device. */
  required: boolean;
  sizes: Array<[number, number]>;
  /** Sort order in the UI (newest/most relevant first). */
  order: number;
}

const pair = (w: number, h: number): Array<[number, number]> => [
  [w, h],
  [h, w],
];

export const SCREENSHOT_SPECS: ScreenshotSpec[] = [
  {
    displayType: 'APP_IPHONE_67',
    label: 'iPhone 6.9"',
    device: 'iphone',
    required: true,
    // App Store Connect keeps the APP_IPHONE_67 API bucket name for the
    // current 6.9-inch screenshot class.
    sizes: [...pair(1260, 2736), ...pair(1290, 2796), ...pair(1320, 2868)],
    order: 1,
  },
  {
    displayType: 'APP_IPHONE_65',
    label: 'iPhone 6.5"',
    device: 'iphone',
    required: false,
    sizes: [...pair(1284, 2778), ...pair(1242, 2688)],
    order: 3,
  },
  {
    displayType: 'APP_IPHONE_61',
    label: 'iPhone 6.3" / 6.1"',
    device: 'iphone',
    required: false,
    sizes: [...pair(1206, 2622), ...pair(1179, 2556), ...pair(1170, 2532), ...pair(828, 1792)],
    order: 4,
  },
  {
    displayType: 'APP_IPHONE_58',
    label: 'iPhone 5.8"',
    device: 'iphone',
    required: false,
    sizes: [...pair(1125, 2436), ...pair(1080, 2340)],
    order: 5,
  },
  {
    displayType: 'APP_IPHONE_55',
    label: 'iPhone 5.5"',
    device: 'iphone',
    required: false,
    sizes: pair(1242, 2208),
    order: 6,
  },
  {
    displayType: 'APP_IPHONE_47',
    label: 'iPhone 4.7"',
    device: 'iphone',
    required: false,
    sizes: pair(750, 1334),
    order: 7,
  },
  {
    displayType: 'APP_IPHONE_40',
    label: 'iPhone 4"',
    device: 'iphone',
    required: false,
    sizes: [[640, 1096], [640, 1136], [1136, 600], [1136, 640]],
    order: 8,
  },
  {
    displayType: 'APP_IPAD_PRO_3GEN_129',
    label: 'iPad 13"',
    device: 'ipad',
    required: true,
    sizes: [...pair(2064, 2752), ...pair(2048, 2732)],
    order: 10,
  },
  {
    displayType: 'APP_IPAD_PRO_3GEN_11',
    label: 'iPad 11"',
    device: 'ipad',
    required: false,
    sizes: [...pair(1488, 2266), ...pair(1668, 2420), ...pair(1668, 2388), ...pair(1640, 2360)],
    order: 11,
  },
  {
    displayType: 'APP_IPAD_PRO_129',
    label: 'iPad 12.9" (2nd gen)',
    device: 'ipad',
    required: false,
    sizes: pair(2048, 2732),
    order: 12,
  },
  {
    displayType: 'APP_IPAD_105',
    label: 'iPad 10.5"',
    device: 'ipad',
    required: false,
    sizes: pair(1668, 2224),
    order: 13,
  },
  {
    displayType: 'APP_IPAD_97',
    label: 'iPad 9.7"',
    device: 'ipad',
    required: false,
    sizes: [
      [1536, 2008],
      [1536, 2048],
      [2048, 1496],
      [2048, 1536],
      [768, 1004],
      [768, 1024],
      [1024, 748],
      [1024, 768],
    ],
    order: 14,
  },
  {
    displayType: 'APP_DESKTOP',
    label: 'Mac',
    device: 'mac',
    required: true,
    // Apple accepts these four Mac screenshot sizes (16:10, landscape).
    sizes: [[1280, 800], [1440, 900], [2560, 1600], [2880, 1800]],
    order: 20,
  },
  {
    displayType: 'APP_APPLE_TV',
    label: 'Apple TV',
    device: 'appletv',
    required: true,
    sizes: [[1920, 1080], [3840, 2160]],
    order: 21,
  },
  {
    displayType: 'APP_APPLE_VISION_PRO',
    label: 'Apple Vision Pro',
    device: 'vision',
    required: true,
    sizes: [[3840, 2160]],
    order: 22,
  },
];

/** Device-size choices shown before App Store Connect has any screenshot sets. */
export function defaultScreenshotSpecs(
  platform: string,
  devices?: ReadonlyArray<'iphone' | 'ipad' | 'mac' | 'appletv' | 'watch' | 'vision'>,
): ScreenshotSpec[] {
  if (platform === 'MAC_OS') return SCREENSHOT_SPECS.filter((spec) => spec.device === 'mac');
  if (platform === 'TV_OS') return SCREENSHOT_SPECS.filter((spec) => spec.device === 'appletv');
  if (platform === 'VISION_OS') return SCREENSHOT_SPECS.filter((spec) => spec.device === 'vision');
  if (platform !== 'IOS') return [];
  const supportsIphone = devices?.includes('iphone') ?? false;
  const supportsIpad = devices?.includes('ipad') ?? false;
  if (supportsIphone && !supportsIpad) return SCREENSHOT_SPECS.filter((spec) => spec.device === 'iphone');
  if (supportsIpad && !supportsIphone) return SCREENSHOT_SPECS.filter((spec) => spec.device === 'ipad');
  // Unreleased iOS apps often have no public device data yet, so show both phone + tablet families.
  return SCREENSHOT_SPECS.filter((spec) => spec.device === 'iphone' || spec.device === 'ipad');
}

const byType = new Map(SCREENSHOT_SPECS.map((s) => [s.displayType, s]));

export function screenshotSpec(displayType: string): ScreenshotSpec | undefined {
  return byType.get(displayType);
}

export function screenshotSpecLabel(displayType: string): string {
  return byType.get(displayType)?.label ?? displayType.replace(/^APP_/, '').replace(/_/g, ' ');
}

/** Device-size sets are independent; retained for compatibility with older clients. */
export function conflictingScreenshotDisplayTypes(displayType: string): string[] {
  void displayType;
  return [];
}

/** Compact accepted-size text for selectors; the full validator still accepts every explicit orientation. */
export function screenshotAcceptedSizesLabel(displayType: string): string {
  const spec = byType.get(displayType);
  if (!spec) return '';
  const portrait = spec.sizes.filter(([width, height]) => height >= width);
  const shown = (portrait.length > 0 ? portrait : spec.sizes).map(([width, height]) => `${width}×${height}`);
  const landscapeAccepted = spec.sizes.some(([width, height]) => width > height);
  return `${shown.join(' / ')}${landscapeAccepted ? ' · landscape also accepted' : ''}`;
}

export function screenshotSpecsMatchingDimensions(width: number, height: number): ScreenshotSpec[] {
  return SCREENSHOT_SPECS.filter((spec) => spec.sizes.some(([w, h]) => w === width && h === height));
}

export const MAX_SCREENSHOTS_PER_SET = 10;
export const MAX_SCREENSHOT_BYTES = 12 * 1024 * 1024;

/**
 * In-app purchase / subscription App Store review screenshots use their OWN size —
 * the legacy 3.5" portrait frame — not any marketing screenshot size. Apple rejects
 * a normal App Store screenshot here, which is why uploads are auto-resized to this.
 */
export const REVIEW_SCREENSHOT_SIZE: readonly [number, number] = [640, 920];
export const MAX_REVIEW_SCREENSHOT_BYTES = 5 * 1024 * 1024;

export function validateReviewScreenshotDimensions(width: number, height: number): string | null {
  const [w, h] = REVIEW_SCREENSHOT_SIZE;
  if (width === w && height === h) return null;
  return `App Review screenshots must be exactly ${w}×${h}. This image is ${width}×${height}.`;
}

export function validateScreenshotDimensions(
  displayType: string,
  width: number,
  height: number,
): string | null {
  const spec = byType.get(displayType);
  if (!spec) return `Unknown screenshot display type: ${displayType}`;
  const ok = spec.sizes.some(([w, h]) => w === width && h === height);
  if (!ok) {
    const accepted = spec.sizes.map(([w, h]) => `${w}×${h}`).join(', ');
    const matching = screenshotSpecsMatchingDimensions(width, height)
      .filter((match) => match.displayType !== displayType)
      .map((match) => match.label);
    return matching.length > 0
      ? `${width}×${height} matches ${matching.join(' or ')}, not ${spec.label}. Use the matching device slot without resizing, or resize to: ${accepted}.`
      : `${width}×${height} is not valid for ${spec.label}. Resize to: ${accepted}.`;
  }
  return null;
}
